// Performance optimization configuration for Botaniksan e-commerce

export const PERFORMANCE_CONFIG = {
  // Lazy loading settings
  lazyLoading: {
    enabled: true,
    rootMargin: "100px 0px", // Load content 100px before it comes into view
    threshold: 0.1, // Trigger when 10% of element is visible
    
    // Component-specific settings
    components: {
      featured: { rootMargin: "200px 0px", priority: "high" },
      bestseller: { rootMargin: "150px 0px", priority: "high" },
      deals: { rootMargin: "100px 0px", priority: "medium" },
      discounted: { rootMargin: "100px 0px", priority: "medium" },
      recentlyAdded: { rootMargin: "50px 0px", priority: "low" },
      footer: { rootMargin: "0px 0px", priority: "low" },
    },
    
    // Image lazy loading
    images: {
      rootMargin: "50px 0px",
      placeholder: {
        enabled: true,
        color: "#f8f9fa",
        showSpinner: true,
      },
      fallback: {
        enabled: true,
        retryAttempts: 2,
      }
    }
  },

  // Loading states configuration
  loadingStates: {
    skeletons: {
      enabled: true,
      animationDuration: "1.5s",
      baseColor: "#f8f9fa",
      highlightColor: "#e9ecef",
    },
    
    fallbacks: {
      default: {
        variant: "default",
        height: "300px",
        showSpinner: true,
      },
      skeleton: {
        variant: "skeleton", 
        height: "400px",
      },
      minimal: {
        variant: "minimal",
        height: "200px",
        showSpinner: false,
      }
    }
  },

  // Error boundaries configuration
  errorBoundaries: {
    enabled: true,
    showRetry: true,
    logErrors: true,
    fallbackUI: {
      title: "Bir hata oluştu",
      message: "Bu bölüm yüklenirken bir sorun yaşandı. Lütfen sayfayı yenileyin veya daha sonra tekrar deneyin.",
      showDetails: process.env.NODE_ENV === "development",
    }
  },

  // Performance monitoring
  monitoring: {
    enabled: process.env.NODE_ENV === "development",
    reportInterval: 5000, // Report every 5 seconds
    metrics: {
      fcp: true, // First Contentful Paint
      lcp: true, // Largest Contentful Paint
      cls: true, // Cumulative Layout Shift
      fid: true, // First Input Delay
      memory: true,
      network: true,
      customMetrics: true,
    },
    reporting: {
      console: true,
      localStorage: true,
      analytics: false, // Set to true to send to analytics service
    }
  },

  // Code splitting settings
  codeSplitting: {
    enabled: true,
    chunkSize: {
      maxInitial: 244 * 1024, // 244KB initial bundle
      maxAsync: 244 * 1024,   // 244KB async chunks
    },
    prefetch: {
      enabled: true,
      priority: ["featured", "bestseller"], // Prefetch high-priority components
    }
  },

  // Image optimization
  images: {
    formats: ["webp", "jpg", "png"],
    qualities: {
      thumbnail: 70,
      medium: 80,
      large: 85,
    },
    sizes: {
      thumbnail: "200x200",
      medium: "400x400", 
      large: "800x800",
    },
    lazyLoading: {
      enabled: true,
      placeholder: true,
      blur: true,
    }
  },

  // Network optimization
  network: {
    timeout: 30000, // 30 seconds timeout
    retries: 3,
    caching: {
      enabled: true,
      maxAge: 300000, // 5 minutes
    },
    compression: {
      enabled: true,
      level: 6,
    }
  },

  // Bundle optimization
  bundle: {
    treeshaking: true,
    minification: true,
    compression: true,
    sourceMaps: process.env.NODE_ENV === "development",
  }
};

// Performance utilities
export const getOptimizationLevel = () => {
  if (typeof window !== "undefined") {
    // Check device capabilities
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const memory = navigator.deviceMemory || 4;
    
    if (connection) {
      const effectiveType = connection.effectiveType;
      
      // Adjust optimization based on connection
      if (effectiveType === "4g" && memory >= 4) {
        return "high";
      } else if (effectiveType === "3g" || memory >= 2) {
        return "medium"; 
      } else {
        return "low";
      }
    }
  }
  
  return "medium"; // Default level
};

export const shouldLazyLoad = (componentName) => {
  const level = getOptimizationLevel();
  const config = PERFORMANCE_CONFIG.lazyLoading.components[componentName];
  
  if (!config) return true;
  
  // Always lazy load on low-end devices
  if (level === "low") return true;
  
  // Skip lazy loading for high priority components on high-end devices
  if (level === "high" && config.priority === "high") {
    return false;
  }
  
  return PERFORMANCE_CONFIG.lazyLoading.enabled;
};

export const getLoadingVariant = (componentName) => {
  const level = getOptimizationLevel();
  
  if (level === "low") {
    return "minimal"; // Lighter loading state for low-end devices
  }
  
  return "skeleton"; // Rich loading state for better UX
};

export default PERFORMANCE_CONFIG;


// WEBPACK FOOTER //
// ./src/config/performance.js